import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import withModal from '../../../../../../components/global/modal/withModal';
import DismissButton from '../../../../../../components/global/DismissButton';
import CardSideGroupPreview from '../CardSideGroupPreview';
import CardSideList from './CardSideList';
import Button from '../../../../../../components/global/Button';
import useVirtualPageView from '../../../../../../apps/pro/services/analytics/useVirtualPageView';

const propTypes = {
  className: PropTypes.string,
  accountCard: PropTypes.shape({
    id: PropTypes.string.isRequired,
    card: PropTypes.shape({
      card_side_groups: PropTypes.shape({
        total: PropTypes.number.isRequired,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            asset_image: PropTypes.shape({
              url: PropTypes.string.isRequired,
            }),
            card_sides: PropTypes.shape({
              total: PropTypes.number.isRequired,
              records: PropTypes.arrayOf(
                PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  asset_image: PropTypes.shape({
                    url: PropTypes.string.isRequired,
                  }),
                }).isRequired,
              ).isRequired,
            }).isRequired,
          }).isRequired,
        ),
      }).isRequired,
    }).isRequired,
  }).isRequired,
  side: PropTypes.string.isRequired,
  onCardSideSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
};

const CardSideSelect = ({
  className,
  accountCard,
  side,
  onClose,
  onCardSideSelect,
  title = 'Select a template...',
}) => {
  const [selectedCardSideGroup, setSelectedCardSideGroup] = useState(null);
  const [loadingCardSide, setLoadingCardSide] = useState(null);
  const classes = classNames({
    'card-side-select': true,
    [className]: !!className,
  });

  const cardSideGroups = accountCard.card.card_side_groups.records;
  const mainSideCardSideGroups = cardSideGroups.filter(
    cardSideGroup => cardSideGroup.side === side,
  );

  const onCardSideGroupSelect = async cardSideGroup => {
    const cardSides = cardSideGroup.card_sides.records;

    // If there is only one CardSide in the CardSideGroup
    // select it immediately.
    if (cardSides.length === 1) {
      const [cardSide] = cardSides;

      setLoadingCardSide(cardSide);
      await onCardSideSelect(cardSide);
      setLoadingCardSide(null);
      onClose();

      return;
    }

    setSelectedCardSideGroup(cardSideGroup);
  };

  useVirtualPageView({
    title: `Create a card - ${title}`,
    path: '/template-select',
  });

  return (
    <div className={classes}>
      <div className="card-element-control-header-dismiss">
        <DismissButton onClick={() => onClose()} />
      </div>
      <div className="card-element-control-header">
        <h3 className="card-element-control-header-title">{title}</h3>
      </div>

      {!selectedCardSideGroup && (
        <div className="card-side-select-card-groups">
          {mainSideCardSideGroups.map(cardSideGroup => {
            const [cardSide] = cardSideGroup.card_sides.records ?? [];

            // Just in case, skip empty CardSideGroups (although it should never happen).
            if (!cardSide) {
              return null;
            }

            return (
              <CardSideGroupPreview
                key={cardSideGroup.id}
                onClick={onCardSideGroupSelect}
                cardSideGroup={{
                  ...cardSideGroup,
                  asset_image:
                    cardSideGroup.asset_image || cardSide.asset_image,
                }}
                displayName
                loading={loadingCardSide?.id === cardSide?.id}
              />
            );
          })}
        </div>
      )}

      {selectedCardSideGroup && (
        <Fragment>
          <div className="card-side-list-scroll">
            <CardSideList
              cardSideGroup={selectedCardSideGroup}
              cardSides={selectedCardSideGroup.card_sides.records}
              onCardSideSelect={async cardSide => {
                await onCardSideSelect(cardSide);
                onClose();
              }}
            />
          </div>
          <Button
            btnStyle="link"
            className="mt-2 text-bold"
            onClick={() => setSelectedCardSideGroup(null)}
          >
            Go back
          </Button>
        </Fragment>
      )}
    </div>
  );
};

CardSideSelect.propTypes = propTypes;

const CARD_SIDE_SELECT_MODAL = 'CARD_SIDE_SELECT_MODAL';

export default withModal(CardSideSelect, {
  name: CARD_SIDE_SELECT_MODAL,
  dismissible: false,
  size: 'sm',
  hideHeader: true,
  className: 'card-side-select-modal modal-floating-panel',
  overlayClassName: 'card-side-select-modal-overlay',
});
